import {
  ECardinality,
  EDatasourceType,
  ESymbolCategory,
  IAction,
  IActionDefinitionDto,
  IActionParameter,
  ISymbol
} from "@kuika/kuika-cl-model";

/** SQL flavour the statement of a datasource's actions is written in. */
export type SqlActionDialect = "sqlServer" | "postgres" | "oracle";

interface ISqlDialectLiterals {
  true: string;
  false: string;
  now: string;
  newGuid: string;
  emptyGuid: string;
}

export interface ISqlActionQueryCopyResult {
  /** The action's statement with every design-time-known parameter inlined. */
  query: string;
  resolvedParameterCount: number;
  totalParameterCount: number;
}

// Mirrors the literals the generic-select converters emit per engine
// (SqlServer/PostgreSql/OracleGenericSelectConverter), so a copied query reads
// the same as the SQL Kuika itself would run.
const DIALECT_LITERALS: Record<SqlActionDialect, ISqlDialectLiterals> = {
  sqlServer: {
    true: "1",
    false: "0",
    now: "GETDATE()",
    newGuid: "NEWID()",
    emptyGuid: "CAST(CAST(0 AS BINARY) AS UNIQUEIDENTIFIER)"
  },
  postgres: {
    true: "true",
    false: "false",
    now: "CURRENT_TIMESTAMP",
    newGuid: "gen_random_uuid()",
    emptyGuid: "'00000000-0000-0000-0000-000000000000'::UUID"
  },
  oracle: {
    true: "1",
    false: "0",
    now: "SYSDATE",
    newGuid: "SYS_GUID()",
    emptyGuid: "HEXTORAW('00000000000000000000000000000000')"
  }
};

// `Typename` is declared as BackEndValueType but carries whatever wrote it: the
// statement type inference stores `EValueType[valueType].toString()`, which yields
// "DateTime" where the union spells it "Datetime", and a SQL `bit` column can arrive
// as "bit" / "bool". Every other consumer of a parameter type in the codebase
// compares lower-cased (`symbol-picker`, `isBooleanActionParameterType`, the C#
// generators); these sets do the same so no spelling is silently skipped.
const QUOTED_VALUE_TYPES: readonly string[] = ["string", "guid", "date", "datetime", "datetimeoffset"];

const TEMPORAL_VALUE_TYPES: readonly string[] = ["date", "datetime", "datetimeoffset"];

const BOOLEAN_VALUE_TYPES: readonly string[] = ["boolean", "bool", "bit"];

const NUMERIC_VALUE_TYPES: readonly string[] = [
  "int16",
  "int32",
  "int64",
  "decimal",
  "double",
  "single",
  "byte",
  "autoincrement"
];

const NUMERIC_LITERAL_PATTERN: RegExp = /^-?\d+(\.\d+)?$/;

const GUID_HEX_PATTERN: RegExp = /^[0-9a-fA-F]{32}$/;

const DATE_ONLY_PATTERN: RegExp = /^\d{4}-\d{2}-\d{2}$/;

// What `k-datetime-picker` writes (`YYYY-MM-DD[THH:mm:ss]`), plus the fraction and offset a
// DateTimeOffset can carry. Both separators are accepted since the value can also be typed.
const DATE_TIME_PATTERN: RegExp =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d{1,9})?)?)\s*(Z|[+-]\d{2}:?\d{2})?$/i;

const WORD_CHARACTER: RegExp = /\w/;

// A PostgreSQL dollar-quoted string opens with `$$` or `$tag$`. Sticky so it can be
// tested at one offset without slicing; `$1` is a positional parameter, not a tag,
// and is rejected because a tag cannot start with a digit.
const POSTGRES_DOLLAR_TAG: RegExp = /\$([A-Za-z_][A-Za-z0-9_]*)?\$/y;

// Oracle's alternative quoting picks its own terminator: `q'[...]'`, `q'{...}'`,
// `q'(...)'`, `q'<...>'`, or any other character repeated (`q'!...!'`).
const ORACLE_QUOTE_TERMINATORS: Record<string, string> = { "[": "]", "{": "}", "(": ")", "<": ">" };

interface ISqlSubstitutionResult {
  query: string;
  resolvedNames: Set<string>;
}

export function getSqlActionDialect(actionType: EDatasourceType | undefined): SqlActionDialect | undefined {
  switch (actionType) {
    case EDatasourceType.MsSql:
    case EDatasourceType.ManagedDb:
    case EDatasourceType.CustomDb:
      return "sqlServer";
    case EDatasourceType.Postgre:
    case EDatasourceType.ManagedDbPostgre:
      return "postgres";
    case EDatasourceType.Oracle:
    case EDatasourceType.ManagedDbOracle:
      return "oracle";
    default:
      return undefined;
  }
}

export function canCopySqlActionQuery(actionDefinition: IActionDefinitionDto | undefined): boolean {
  if (!actionDefinition) {
    return false;
  }
  const dialect: SqlActionDialect | undefined = getSqlActionDialect(actionDefinition.actionType);
  if (dialect === undefined) {
    return false;
  }
  return typeof actionDefinition.statement === "string" && actionDefinition.statement.trim() !== "";
}

/**
 * Builds the action's SQL with the parameter values entered on this screen already
 * substituted, so it can be pasted straight into a SQL IDE. Parameters whose value
 * is only known at runtime (component, screen input, bag, action result, "Now" of a
 * user context) keep their `@name` / `:name` placeholder — the reader fills those in.
 */
export function buildSqlActionQueryWithParameters(
  actionDefinition: IActionDefinitionDto,
  action: IAction
): ISqlActionQueryCopyResult {
  const statement: string = actionDefinition.statement ?? "";
  const dialect: SqlActionDialect | undefined = getSqlActionDialect(actionDefinition.actionType);
  const parameters: IActionParameter[] = action?.parameters ?? [];

  if (dialect === undefined) {
    return { query: statement, resolvedParameterCount: 0, totalParameterCount: parameters.length };
  }

  const literalsByName: Map<string, string> = new Map<string, string>();
  parameters.forEach((parameter: IActionParameter): void => {
    const literal: string | undefined = getParameterSqlLiteral(parameter, dialect);
    if (literal !== undefined && parameter.name) {
      literalsByName.set(parameter.name, literal);
    }
  });

  const substitution: ISqlSubstitutionResult = substituteParameterReferences(statement, literalsByName, dialect);

  return {
    query: substitution.query,
    resolvedParameterCount: substitution.resolvedNames.size,
    totalParameterCount: parameters.length
  };
}

function getParameterSqlLiteral(parameter: IActionParameter, dialect: SqlActionDialect): string | undefined {
  const symbol: ISymbol | undefined = parameter?.symbol;
  if (!symbol || !parameter.name) {
    return undefined;
  }
  // A list or an object has no single-literal form to inline.
  if (parameter.cardinality === ECardinality.Many || parameter.isObject === true) {
    return undefined;
  }
  if (symbol.category === ESymbolCategory.default) {
    return getDefaultValueSqlLiteral(symbol.defaultValueName, dialect);
  }
  if (symbol.category === ESymbolCategory.fixed) {
    return getFixedValueSqlLiteral(symbol.fixedValue, parameter.Typename, dialect);
  }
  return undefined;
}

function getDefaultValueSqlLiteral(
  defaultValueName: string | undefined,
  dialect: SqlActionDialect
): string | undefined {
  if (!defaultValueName) {
    return undefined;
  }
  const literals: ISqlDialectLiterals = DIALECT_LITERALS[dialect];
  switch (defaultValueName.trim().toLowerCase()) {
    case "null":
      return "NULL";
    case "empty":
      return "''";
    case "space":
      return "' '";
    case "true":
      return literals.true;
    case "false":
      return literals.false;
    case "now":
    case "datenow":
      return literals.now;
    case "new guid":
    case "newguid":
      return literals.newGuid;
    case "empty guid":
    case "emptyguid":
      return literals.emptyGuid;
    default:
      return undefined;
  }
}

function getFixedValueSqlLiteral(
  fixedValue: unknown,
  typeName: string | undefined,
  dialect: SqlActionDialect
): string | undefined {
  const literals: ISqlDialectLiterals = DIALECT_LITERALS[dialect];
  const normalizedTypeName: string = typeName?.toLowerCase() ?? "";

  if (BOOLEAN_VALUE_TYPES.includes(normalizedTypeName)) {
    if (fixedValue === undefined || fixedValue === null || fixedValue === "") {
      return undefined;
    }
    return String(fixedValue).toLowerCase() === "true" ? literals.true : literals.false;
  }

  if (fixedValue === undefined || fixedValue === null) {
    return "NULL";
  }
  // Objects would stringify to "[object Object]"; leave the placeholder instead.
  if (typeof fixedValue === "object") {
    return undefined;
  }

  const rawValue: string = String(fixedValue);

  if (NUMERIC_VALUE_TYPES.includes(normalizedTypeName)) {
    const trimmedValue: string = rawValue.trim();
    // Anything that is not a plain number gets quoted rather than pasted bare, so the
    // reader sees the value they typed instead of a syntax error with no source.
    return NUMERIC_LITERAL_PATTERN.test(trimmedValue) ? trimmedValue : quoteSqlLiteral(rawValue);
  }

  if (QUOTED_VALUE_TYPES.includes(normalizedTypeName)) {
    if (dialect === "oracle") {
      const oracleLiteral: string | undefined = getOracleTypedLiteral(rawValue, normalizedTypeName);
      if (oracleLiteral !== undefined) {
        return oracleLiteral;
      }
    }
    return quoteSqlLiteral(rawValue);
  }

  // Binary, image, array and untyped values have no readable literal form.
  return undefined;
}

/**
 * Oracle does not implicitly convert the shapes the designer writes: a dashed GUID is not valid
 * hex for a `RAW(16)` column (ORA-01465), and a quoted date is read against `NLS_DATE_FORMAT`,
 * so it parses on one session and fails on the next (ORA-01861). Both get an explicit literal.
 * Anything that does not match falls back to the plain quoted value, so the reader still sees
 * what they typed instead of a literal built from a value we did not understand.
 */
function getOracleTypedLiteral(rawValue: string, normalizedTypeName: string): string | undefined {
  if (normalizedTypeName === "guid") {
    return getOracleRawLiteral(rawValue);
  }
  if (TEMPORAL_VALUE_TYPES.includes(normalizedTypeName)) {
    return getOracleTemporalLiteral(rawValue);
  }
  return undefined;
}

function getOracleRawLiteral(rawValue: string): string | undefined {
  const compactValue: string = rawValue.trim().replace(/[{}-]/g, "");
  if (!GUID_HEX_PATTERN.test(compactValue)) {
    return undefined;
  }
  // Hex only, so the Turkish-i rule cannot bite; `toUpperCase` is locale-independent regardless.
  return `HEXTORAW('${compactValue.toUpperCase()}')`;
}

/**
 * Chooses the literal from the value's shape rather than the declared type: a `Datetime`
 * parameter can hold a date-only value, and `TIMESTAMP '2026-01-01'` is not a valid Oracle
 * literal — the same reason every type comparison here is lower-cased rather than trusted.
 */
function getOracleTemporalLiteral(rawValue: string): string | undefined {
  const trimmedValue: string = rawValue.trim();
  if (DATE_ONLY_PATTERN.test(trimmedValue)) {
    return `DATE '${trimmedValue}'`;
  }

  const parts: RegExpExecArray | null = DATE_TIME_PATTERN.exec(trimmedValue);
  if (parts === null) {
    return undefined;
  }

  const datePart: string = parts[1];
  // Oracle's TIMESTAMP literal needs seconds; the picker omits them for a bare `HH:mm`.
  const timePart: string = parts[2].length === 5 ? `${parts[2]}:00` : parts[2];
  const zone: string | undefined = parts[3];
  if (zone === undefined) {
    return `TIMESTAMP '${datePart} ${timePart}'`;
  }
  return `TIMESTAMP '${datePart} ${timePart} ${normalizeUtcOffset(zone)}'`;
}

/** Oracle wants `+03:00`; the value can arrive as `Z` or `+0300`. */
function normalizeUtcOffset(zone: string): string {
  if (zone.toUpperCase() === "Z") {
    return "+00:00";
  }
  return zone.includes(":") ? zone : `${zone.slice(0, 3)}:${zone.slice(3)}`;
}

function quoteSqlLiteral(value: string): string {
  return `'${value.replace(/'/g, "''")}'`;
}

/**
 * Walks the statement once, substituting `@name` / `:name` in code positions only.
 * A `@name` sitting inside a string literal, a comment or a quoted identifier is text,
 * not a parameter reference: replacing it there broke the literal's quoting (or silently
 * rewrote its content when the value needed no quotes), so those spans are copied through
 * untouched. Both prefixes are accepted because the statement is hand-written and its
 * driver decides the syntax (SqlClient takes `@`, Npgsql and Oracle take `:`).
 *
 * Substituting in one pass also keeps an inlined value from being re-read as SQL, which a
 * per-parameter pass did whenever one value happened to contain another parameter's name.
 */
function substituteParameterReferences(
  statement: string,
  literalsByName: Map<string, string>,
  dialect: SqlActionDialect
): ISqlSubstitutionResult {
  // Longest first so `@TipKodu` is never matched as `@Tip` followed by stray text.
  // Sort is stable, so equal-length names keep parameter order and stay deterministic.
  const names: string[] = [...literalsByName.keys()].sort(
    (left: string, right: string): number => right.length - left.length
  );
  const resolvedNames: Set<string> = new Set<string>();
  const parts: string[] = [];
  let index: number = 0;

  while (index < statement.length) {
    const skipEnd: number = scanNonCodeSpan(statement, index, dialect);
    if (skipEnd > index) {
      parts.push(statement.slice(index, skipEnd));
      index = skipEnd;
      continue;
    }

    const character: string = statement[index];
    if (character === "@" || character === ":") {
      const name: string | undefined = matchParameterName(statement, index, names);
      if (name !== undefined) {
        parts.push(literalsByName.get(name) ?? "");
        resolvedNames.add(name);
        index += name.length + 1;
        continue;
      }
    }

    parts.push(character);
    index += 1;
  }

  return { query: parts.join(""), resolvedNames };
}

/**
 * Returns the end of the non-code span starting at `index`, or `index` itself when the
 * position opens no such span. Every span is recognised on the original statement, so a
 * `--` inside a string never opens a comment and a quote inside a comment never opens a
 * string.
 */
function scanNonCodeSpan(statement: string, index: number, dialect: SqlActionDialect): number {
  const character: string = statement[index];
  const nextCharacter: string = statement[index + 1] ?? "";

  if (character === "-" && nextCharacter === "-") {
    const lineEnd: number = statement.indexOf("\n", index + 2);
    return lineEnd === -1 ? statement.length : lineEnd;
  }
  if (character === "/" && nextCharacter === "*") {
    return scanBlockComment(statement, index, dialect);
  }
  // `E'...'` reads backslash escapes, so a naive `''`-only scan would close it early and
  // mis-classify the rest of the statement.
  if (dialect === "postgres" && opensPrefixedLiteral(statement, index, "e")) {
    return scanDelimitedSpan(statement, index + 1, "'", true);
  }
  if (dialect === "oracle" && opensAlternativeQuotedLiteral(statement, index)) {
    return scanAlternativeQuotedLiteral(statement, index);
  }
  if (character === "'") {
    return scanDelimitedSpan(statement, index, "'", false);
  }
  // A quoted identifier in PostgreSQL/Oracle; under `QUOTED_IDENTIFIER OFF` a string in
  // SQL Server. Neither can hold a parameter reference.
  if (character === '"') {
    return scanDelimitedSpan(statement, index, '"', false);
  }
  if (character === "[" && dialect === "sqlServer") {
    return scanBracketedIdentifier(statement, index);
  }
  if (character === "$" && dialect === "postgres") {
    return scanDollarQuotedLiteral(statement, index);
  }
  return index;
}

/** SQL Server and PostgreSQL nest block comments; Oracle closes at the first terminator. */
function scanBlockComment(statement: string, index: number, dialect: SqlActionDialect): number {
  const nests: boolean = dialect !== "oracle";
  let depth: number = 1;
  let cursor: number = index + 2;

  while (cursor < statement.length) {
    if (nests && statement[cursor] === "/" && statement[cursor + 1] === "*") {
      depth += 1;
      cursor += 2;
      continue;
    }
    if (statement[cursor] === "*" && statement[cursor + 1] === "/") {
      depth -= 1;
      cursor += 2;
      if (depth === 0) {
        return cursor;
      }
      continue;
    }
    cursor += 1;
  }
  return statement.length;
}

/** Scans `'...'` / `"..."`, where the delimiter is escaped by doubling it. */
function scanDelimitedSpan(
  statement: string,
  index: number,
  delimiter: string,
  readsBackslashEscapes: boolean
): number {
  let cursor: number = index + 1;

  while (cursor < statement.length) {
    const character: string = statement[cursor];
    if (readsBackslashEscapes && character === "\\") {
      cursor += 2;
      continue;
    }
    if (character === delimiter) {
      if (statement[cursor + 1] === delimiter) {
        cursor += 2;
        continue;
      }
      return cursor + 1;
    }
    cursor += 1;
  }
  // Unterminated: the rest of the statement is inside the literal either way.
  return statement.length;
}

/** Scans `[...]`, where `]]` escapes a closing bracket. */
function scanBracketedIdentifier(statement: string, index: number): number {
  let cursor: number = index + 1;

  while (cursor < statement.length) {
    if (statement[cursor] === "]") {
      if (statement[cursor + 1] === "]") {
        cursor += 2;
        continue;
      }
      return cursor + 1;
    }
    cursor += 1;
  }
  return statement.length;
}

/** Scans `$tag$ ... $tag$`, which holds function bodies verbatim and escapes nothing. */
function scanDollarQuotedLiteral(statement: string, index: number): number {
  POSTGRES_DOLLAR_TAG.lastIndex = index;
  const opening: RegExpExecArray | null = POSTGRES_DOLLAR_TAG.exec(statement);
  if (opening === null) {
    return index;
  }
  const tag: string = opening[0];
  const closing: number = statement.indexOf(tag, index + tag.length);
  return closing === -1 ? statement.length : closing + tag.length;
}

/** Scans Oracle's `q'X ... Y'`, whose terminator the statement itself picks. */
function scanAlternativeQuotedLiteral(statement: string, index: number): number {
  const quoteIndex: number = statement.indexOf("'", index);
  const opener: string = statement[quoteIndex + 1] ?? "";
  if (opener === "") {
    return statement.length;
  }
  const terminator: string = `${ORACLE_QUOTE_TERMINATORS[opener] ?? opener}'`;
  const closing: number = statement.indexOf(terminator, quoteIndex + 2);
  return closing === -1 ? statement.length : closing + terminator.length;
}

/** True when `index` starts a `<letter>'...'` literal rather than an identifier ending in it. */
function opensPrefixedLiteral(statement: string, index: number, prefix: string): boolean {
  if (statement[index]?.toLowerCase() !== prefix || statement[index + 1] !== "'") {
    return false;
  }
  return !isWordCharacter(statement[index - 1]);
}

/** True for `q'`, including the national-charset form `nq'`. */
function opensAlternativeQuotedLiteral(statement: string, index: number): boolean {
  if (opensPrefixedLiteral(statement, index, "q")) {
    return true;
  }
  const isNationalPrefix: boolean =
    statement[index]?.toLowerCase() === "n" &&
    statement[index + 1]?.toLowerCase() === "q" &&
    statement[index + 2] === "'";
  return isNationalPrefix && !isWordCharacter(statement[index - 1]);
}

/**
 * Matches the parameter referenced at `index`. The reference must stand on its own: the
 * character before it is neither a word character nor a second `:` — so a PostgreSQL cast
 * such as `value::date` is not read as `:date` — and the name must not run into one.
 */
function matchParameterName(statement: string, index: number, names: string[]): string | undefined {
  const previous: string | undefined = index === 0 ? undefined : statement[index - 1];
  if (previous !== undefined && (isWordCharacter(previous) || previous === ":")) {
    return undefined;
  }

  return names.find((name: string): boolean => {
    if (!statement.startsWith(name, index + 1)) {
      return false;
    }
    return !isWordCharacter(statement[index + 1 + name.length]);
  });
}

function isWordCharacter(character: string | undefined): boolean {
  return character !== undefined && WORD_CHARACTER.test(character);
}
